import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { inputCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime } from "@/lib/format";

const TYPE_LABEL = { salary: "Salary", fixed_consult: "Fixed / consult", percentage: "Percentage" };

export default function DoctorEarnings() {
  const { can } = useAuth();
  const canSettle = can("hospital.earnings.manage");
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState({});

  const load = useCallback(async () => {
    setData(null);
    try {
      const [e, s] = await Promise.all([
        api.get("/hospital/earnings", { params: { status: status || undefined } }),
        api.get("/hospital/earnings/summary"),
      ]);
      setData(e.data.items); setSummary(s.data);
    } catch (err) { setError(apiError(err)); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const settle = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) { toast.error("Select earnings to settle."); return; }
    try {
      const { data: r } = await api.post("/hospital/earnings/settle", { earning_ids: ids, reference: "" });
      toast.success(`${r.settled} earning(s) settled.`); setSel({}); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!can("hospital.earnings.view")) return <NoAccess what="doctor earnings" />;

  return (
    <div data-testid="doctor-earnings-page">
      <PageHeader overline="Hospital" title="Doctor Earnings"
        subtitle="Consultation-based doctor compensation ledger, kept separate from hospital, pharmacy and B2B revenue."
        actions={canSettle && <button onClick={settle} className={primaryBtnCls} data-testid="settle-earnings-btn">Settle selected</button>} />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="panel p-4" data-testid="earnings-total"><p className="text-xs text-slate-400">Total doctor earnings (₹)</p><p className="text-xl font-semibold">{summary.total_earnings.toLocaleString("en-IN")}</p></div>
          <div className="panel p-4" data-testid="earnings-unpaid"><p className="text-xs text-slate-400">Unpaid (₹)</p><p className="text-xl font-semibold text-rose-600">{summary.total_unpaid.toLocaleString("en-IN")}</p></div>
          <div className="panel p-4"><p className="text-xs text-slate-400">Doctors</p><p className="text-xl font-semibold">{summary.rows.length}</p></div>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="earnings-status-filter">
          <option value="">All</option><option value="unpaid">Unpaid</option><option value="paid">Paid</option>
        </select>
      </div>

      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <Table testid="earnings-table" columns={[canSettle ? "✓" : "", "Doctor", "Patient", "Service", "Base ₹", "Doctor ₹", "Type", "Status", "Date"]}
          rows={data} empty={<EmptyState title="No earnings yet" subtitle="Doctor earnings appear here as consultations are billed." testid="earnings-empty" />}
          renderRow={(r) => (
            <tr key={r.earning_id} data-testid={`earning-row-${r.earning_id}`}>
              <Td>{canSettle && r.status === "unpaid" && <input type="checkbox" checked={!!sel[r.earning_id]} onChange={(e) => setSel({ ...sel, [r.earning_id]: e.target.checked })} data-testid={`earning-check-${r.earning_id}`} />}</Td>
              <Td className="font-medium text-slate-900">Dr. {r.doctor_name}</Td>
              <Td className="text-slate-600">{r.patient_name}<span className="block text-xs text-slate-400">{r.patient_uhid}</span></Td>
              <Td className="text-slate-600 text-xs">{r.service}<span className="block text-slate-400">{r.rate_desc}</span></Td>
              <Td>₹{r.base_amount}</Td>
              <Td className="font-semibold text-[#EA580C]">₹{r.doctor_amount}</Td>
              <Td className="text-xs">{TYPE_LABEL[r.compensation_type] || r.compensation_type}</Td>
              <Td><span className={`text-xs font-medium ${r.status === "paid" ? "text-emerald-600" : "text-rose-600"}`}>{r.status}</span></Td>
              <Td className="text-xs text-slate-400">{dmyTime(r.created_at)}</Td>
            </tr>
          )} />
      )}
    </div>
  );
}
